/**
 * @fileoverview Body HTML for exporting the Gallagher insurance notification.
 *
 * The Gallagher "document" is a web-portal form with no template HTML, so the
 * on-screen preview renders the resolved field values as a table. The Word /
 * PDF export paths build their body from template HTML and produced an EMPTY
 * document for this precedent (MOR-2342: a 2.9 KB .docx whose altChunk body
 * was `<body></body>`). This helper renders the same field table as the
 * preview so the download carries what the analyst reviewed.
 */

import { GALLAGHER_INSURANCE_FIELDS } from "@/lib/precedent-templates";
import type { GallagherFieldConfig } from "~/convex/jobs/precedents/gallagherFieldConfig";

/** Escape the five HTML-significant characters for text content. */
export function escapeHtmlText(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * The rows the Gallagher preview shows: every non-hidden portal field with
 * the value saved under its own key, falling back to its canonical source.
 */
export function gallagherExportRows(
  values: Record<string, string>,
  fieldConfig?: GallagherFieldConfig[],
): Array<{ key: string; label: string; value: string }> {
  const hidden = new Set(
    (fieldConfig ?? [])
      .filter((entry) => entry.hidden)
      .map((entry) => entry.fieldKey),
  );
  return GALLAGHER_INSURANCE_FIELDS.filter(
    (field) => !hidden.has(field.key),
  ).map((field) => ({
    key: field.key,
    label: field.label,
    value:
      values[field.key] ??
      (field.canonicalKey ? values[field.canonicalKey] : undefined) ??
      "",
  }));
}

/**
 * A self-contained two-column table (label / value) of the Gallagher fields,
 * suitable as the `bodyHtml` of a LetterParts export. Values are escaped;
 * multi-line values keep their line breaks.
 */
export function buildGallagherExportHtml(
  values: Record<string, string>,
  opts: { title?: string; fieldConfig?: GallagherFieldConfig[] } = {},
): string {
  const rows = gallagherExportRows(values, opts.fieldConfig)
    .map(
      ({ label, value }) =>
        `<tr><td style="width:40%;padding:4pt 6pt;border:1px solid #bfbfbf;vertical-align:top;font-weight:bold">${escapeHtmlText(label)}</td><td style="padding:4pt 6pt;border:1px solid #bfbfbf;vertical-align:top">${escapeHtmlText(value).replace(/\r?\n/g, "<br />")}</td></tr>`,
    )
    .join("");
  const heading = opts.title
    ? `<h2 style="font-size:14pt;margin:0 0 8pt 0">${escapeHtmlText(opts.title)}</h2>`
    : "";
  return `${heading}<table style="width:100%;border-collapse:collapse;font-size:10pt">${rows}</table>`;
}
